import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { numberSchemes, type NumberScheme, type ResetPeriod } from '@/api/numberSchemes';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

interface Editing {
  id?: string;
  document_type: string;
  organization_id: string | null;
  prefix: string;
  include_year: boolean;
  include_month: boolean;
  include_org_code: boolean;
  counter_width: number;
  separator: string;
  reset_period: ResetPeriod;
  per_organization: boolean;
  is_active: boolean;
}

// Локалізовані назви типів документів для дропдауна.
// Згруповано за категоріями для зручності пошуку.
const DOC_TYPE_LABELS: Record<string, string> = {
  // ===== Каса / Банк =====
  'doc.cash_receipts': 'ПКО (прибутковий касовий)',
  'doc.cash_payments': 'РКО (видатковий касовий)',
  'doc.bank_receipts': 'Банк — надходження',
  'doc.bank_payments': 'Банк — платежі',
  'doc.payment_orders': 'Платіжний ордер',
  'doc.currency_purchase_sale': 'Купівля / продаж валюти',
  'doc.advance_reports': 'Авансовий звіт',
  'doc.manual_operations': 'Бухгалтерська довідка',
  'doc.fx_revaluations': 'Переоцінка валюти',

  // ===== Товари / Склад =====
  'doc.goods_receipts': 'Прибуткова накладна',
  'doc.goods_sales': 'Видаткова накладна',
  'doc.service_acts': 'Акт виконаних робіт/послуг',
  'doc.tnn': 'Товарно-транспортна накладна (ТТН)',
  'doc.goods_transfers': 'Переміщення товарів',
  'doc.goods_writeoffs': 'Акт списання товарів',
  'doc.goods_recognitions': 'Оприбуткування лишків',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.inventory_counts': 'Інвентаризація товарів',
  'doc.warehouse_orders': 'Складський ордер',
  'doc.customs_declarations': 'ВМД (митна декларація)',
  'doc.additional_expenses': 'Додаткові витрати на товар',
  'doc.customer_orders': 'Замовлення від покупця (legacy)',
  'doc.customer_orders.order': 'Замовлення покупця',
  'doc.customer_orders.invoice': 'Рахунок на оплату',
  'doc.supplier_orders': 'Замовлення постачальнику (legacy)',
  'doc.supplier_orders.order': 'Замовлення постачальнику',
  'doc.supplier_orders.invoice': 'Рахунок від постачальника',

  // ===== ОЗ / НМА / МНМА =====
  'ref.fixed_assets_v2': 'Картка ОЗ',
  'ref.intangible_assets_v2': 'Картка НМА',
  'ref.low_value_assets': 'Картка МНМА',
  'doc.asset_commissioning': 'Введення ОЗ в експлуатацію',
  'doc.asset_write_off': 'Акт списання ОЗ',
  'doc.asset_internal_transfer': 'Переміщення ОЗ',
  'doc.fixed_asset_modernization': 'Модернізація ОЗ',
  'doc.depreciation_calculation': 'Розрахунок амортизації',
  // Legacy / deprecated:
  'doc.fa_commissionings': 'Введення ОЗ (legacy)',
  'doc.fa_depreciations': 'Амортизація (legacy)',
  'doc.fa_disposals': 'Вибуття ОЗ (legacy)',

  // ===== Зарплата / HR =====
  'doc.timesheets': 'Табель робочого часу',
  'doc.payrolls': 'Нарахування зарплати',
  'doc.payroll_payments': 'Виплата зарплати',
  'doc.vacation_orders': 'Наказ на відпустку',
  'doc.vacation_calculations': 'Розрахунок відпускних',
  'doc.sick_leaves': 'Лікарняний лист',
  'doc.sick_leave_calculations': 'Розрахунок лікарняного',
  'doc.hire_orders': 'Прийом на роботу',
  'doc.staff_transfers': 'Кадрове переміщення',
  'doc.dismissal_orders': 'Звільнення',

  // ===== ПДВ / Податки =====
  'doc.tax_purpose_adjustments_inventory': 'Зміна призначення запасів (ПДВ)',
  'doc.tax_purpose_adjustments_tzr': 'Зміна призначення ТЗР (ПДВ)',
  'doc.expected_vs_confirmed_vat_adjustments': 'Коригування ПДВ',
  'doc.advance_payment_tax_registration': 'ПН на аванс',
  'doc.tax_invoice_appendix_1': 'Додаток 1 до ПН',
  'doc.tax_invoice_appendix_2': 'Додаток 2 до ПН',
  'doc.proportional_vat_recalculation': 'Перерахунок пропорційного ПДВ',
  'doc.proportional_vat_coefficient': 'Коефіцієнт пропорційного ПДВ',
  'doc.income_tax_expense_normalization': 'Нормування витрат (податок на прибуток)',
  'doc.income_tax_calculation': 'Розрахунок податку на прибуток',

  // ===== ФОП =====
  'doc.single_tax_calculations': 'Єдиний податок (ФОП)',
  'doc.fop_self_esv_payments': 'ЄСВ ФОП',
  'doc.fop_self_wz_payments': 'Військовий збір ФОП',
  'doc.fop_zso_quarterly_calculations': 'ЗСО ФОП квартальний',

  // ===== Розрахунки / Заборгованість =====
  'doc.debt_adjustments': 'Взаємозалік / коригування боргу',
  'doc.doubtful_debt_reserves': 'Резерв сумнівних боргів',
  'doc.reconciliation_acts': 'Акт звірки',
  'doc.powers_of_attorney': 'Довіреність',
  'doc.settlement_inventories': 'Інвентаризація розрахунків',

  // ===== Виробництво =====
  'doc.production_reports': 'Звіт виробництва за зміну',
  'doc.product_assemblies': 'Комплектація номенклатури',
  'doc.material_transfer_for_processing': 'Передача сировини у переробку',
  'doc.material_receipt_for_processing': 'Прийняття чужої сировини',
  'doc.processing_receipts': 'Поступлення з переробки',
  'doc.processing_services_sales': 'Реалізація послуг переробки',
  'doc.production_service_acts': 'Акт виробничих послуг',
  'doc.wip_inventory': 'Інвентаризація НЗВ',

  // ===== Роздріб / POS =====
  'doc.retail_sales': 'Роздрібний продаж',
  'doc.retail_returns': 'Роздрібне повернення',
  'doc.cash_shift_open': 'Відкриття касової зміни',
  'doc.cash_shift_close': 'Закриття касової зміни (Z-звіт)',
  'doc.customer_card_issue': 'Видача дисконтної картки',
  'doc.loyalty_transactions': 'Транзакція бонусів',
  'doc.online_store_order': 'Замовлення з онлайн-магазину',
  'doc.price_change': 'Зміна цін',

  // ===== Комісія =====
  'ref.commission_contract': 'Договір комісії',
  'doc.commission_goods_transfer': 'Передача товарів на комісію',
  'doc.commission_sale': 'Комісійний продаж',
  'doc.commission_settlement_report': 'Звіт комітента',

  // ===== Системні =====
  'doc.opening_balances': 'Вступні залишки',
  'doc.month_close_runs': 'Закриття місяця',
};

const RESET_LABELS: Record<ResetPeriod, string> = {
  NEVER: 'Ніколи (наскрізна)',
  YEAR: 'Щороку',
  MONTH: 'Щомісяця',
};

function emptyEditing(docType: string, defaultPrefix = ''): Editing {
  return {
    document_type: docType,
    organization_id: null,
    prefix: defaultPrefix,
    include_year: true,
    include_month: false,
    include_org_code: false,
    counter_width: 4,
    separator: '-',
    reset_period: 'YEAR',
    per_organization: true,
    is_active: true,
  };
}

export default function NumberSchemesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const docTypesQ = useQuery({
    queryKey: ['doc-types'],
    queryFn: numberSchemes.listDocumentTypes,
  });
  const defaultsQ = useQuery({
    queryKey: ['default-prefixes'],
    queryFn: numberSchemes.defaultPrefixes,
  });
  const schemesQ = useQuery({
    queryKey: ['number-schemes'],
    queryFn: () => numberSchemes.list(),
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    orgsQ.data?.items.forEach((o) => m.set(o.id, `${o.code} — ${o.name}`));
    return m;
  }, [orgsQ.data]);

  const save = useMutation({
    mutationFn: () => editing!.id
      ? numberSchemes.update(editing!.id, editing!)
      : numberSchemes.create(editing!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['number-schemes'] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => numberSchemes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['number-schemes'] }),
  });

  // Preview live при редактировании
  const previewQ = useQuery({
    queryKey: ['preview', editing?.document_type, editing?.organization_id, editing?.prefix,
               editing?.include_year, editing?.include_month, editing?.include_org_code,
               editing?.counter_width, editing?.separator,
               editing?.reset_period, editing?.per_organization, editing?.id],
    queryFn: () => editing?.id
      ? numberSchemes.preview({
          document_type: editing.document_type,
          organization_id: editing.organization_id ?? undefined,
        })
      : Promise.resolve(null),
    enabled: !!editing?.id,
  });

  // Сортуємо за людиночитаною назвою (а не за системним кодом),
  // щоб дропдоун був інтуїтивний для бухгалтера.
  const docTypes = useMemo(() => {
    const list = docTypesQ.data ?? [];
    return [...list].sort((a, b) => {
      const labelA = DOC_TYPE_LABELS[a] ?? a;
      const labelB = DOC_TYPE_LABELS[b] ?? b;
      return labelA.localeCompare(labelB, 'uk');
    });
  }, [docTypesQ.data]);

  // Колонки списку схем нумерації — у спільному стилі DataTable.
  const cols = useMemo<ColumnDef<NumberScheme>[]>(() => [
    {
      key: 'document_type',
      header: 'Тип документа',
      sortValue: (s) => DOC_TYPE_LABELS[s.document_type] ?? s.document_type,
      cell: (s) => DOC_TYPE_LABELS[s.document_type] ?? s.document_type,
    },
    {
      key: 'organization',
      header: 'Організація',
      sortValue: (s) =>
        s.organization_id
          ? (orgMap.get(s.organization_id) ?? s.organization_id)
          : '',
      cell: (s) =>
        s.organization_id
          ? (orgMap.get(s.organization_id) ?? s.organization_id.slice(0, 8))
          : <em style={{ color: 'var(--muted)' }}>(всі)</em>,
    },
    {
      key: 'prefix',
      header: 'Префікс',
      width: 110,
      sortValue: (s) => s.prefix ?? '',
      cell: (s) => <span style={{ fontFamily: 'monospace' }}>{s.prefix || '—'}</span>,
    },
    {
      key: 'format',
      header: 'Формат',
      cell: (s) => (
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {[
            s.prefix,
            s.include_org_code && '<org>',
            s.include_year && '<рік>',
            s.include_month && '<міс>',
            '0'.repeat(s.counter_width),
          ].filter(Boolean).join(s.separator)}
        </span>
      ),
    },
    {
      key: 'reset',
      header: 'Скид',
      width: 130,
      sortValue: (s) => RESET_LABELS[s.reset_period as ResetPeriod],
      cell: (s) => RESET_LABELS[s.reset_period as ResetPeriod],
    },
    {
      key: 'is_active',
      header: 'Активна',
      width: 90,
      align: 'center',
      sortValue: (s) => (s.is_active ? 1 : 0),
      cell: (s) => (s.is_active ? '✓' : '—'),
    },
    {
      key: 'actions',
      header: '',
      width: 110,
      align: 'right',
      cell: (s) => (
        <>
          <button onClick={() => setEditing({ ...s } as Editing)}>✎</button>
          <button className="danger" style={{ marginLeft: '0.3rem' }}
                  onClick={() => confirm('Видалити схему?').then((ok) => { if (ok) remove.mutate(s.id); })}>
            ×
          </button>
        </>
      ),
    },
  ], [orgMap, confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Налаштування нумерації документів</h2>
        <button className="primary"
                onClick={() => {
                  const dt = docTypes[0] ?? 'doc.timesheets';
                  setEditing(emptyEditing(dt, defaultsQ.data?.[dt] ?? ''));
                }}>
          + Нова схема
        </button>
      </div>

      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Якщо схеми нема — використовується дефолт: <code>ПРЕФІКС-РІК-NNNN</code> (напр. TS-2026-0001),
        скид щороку, окремо по організаціях. Глобальна схема (без організації) діє для всіх
        організацій, якщо для конкретної не створено окремої.
      </p>

      {editing && (
        <div style={{ background: 'var(--bg-soft-2)', padding: '1rem',
                      borderRadius: 'var(--radius-lg)', marginBottom: '1rem',
                      border: '1px solid var(--border)' }}>
          <h3 style={{ marginTop: 0 }}>
            {editing.id ? 'Редагування схеми' : 'Нова схема'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="field">
              <label>Тип документа</label>
              <select value={editing.document_type}
                      disabled={!!editing.id}
                      onChange={(e) => {
                        const dt = e.target.value;
                        // Якщо поточний префікс порожній або співпадає з дефолтом
                        // попереднього типу — підставляємо дефолтний для нового типу.
                        const oldDefault = defaultsQ.data?.[editing.document_type] ?? '';
                        const newDefault = defaultsQ.data?.[dt] ?? '';
                        const nextPrefix =
                          (!editing.prefix || editing.prefix === oldDefault)
                            ? newDefault
                            : editing.prefix;
                        setEditing({ ...editing, document_type: dt, prefix: nextPrefix });
                      }}>
                {docTypes.map((dt) => (
                  <option key={dt} value={dt}>{DOC_TYPE_LABELS[dt] ?? dt}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Організація</label>
              <select value={editing.organization_id ?? ''}
                      disabled={!!editing.id}
                      onChange={(e) => setEditing({ ...editing, organization_id: e.target.value || null })}>
                <option value="">— Глобальна (для всіх) —</option>
                {orgsQ.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>
                Префікс
                {defaultsQ.data?.[editing.document_type] && (
                  <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '0.7rem' }}>
                    &nbsp;(дефолт: {defaultsQ.data[editing.document_type]})
                  </span>
                )}
              </label>
              <input value={editing.prefix}
                     placeholder={defaultsQ.data?.[editing.document_type] ?? 'напр. TS'}
                     onChange={(e) => setEditing({ ...editing, prefix: e.target.value })} />
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.2rem' }}>
                Якщо залишити порожнім — буде використано дефолтний префікс типу документа.
              </div>
            </div>
            <div className="field">
              <label>Роздільник</label>
              <input value={editing.separator}
                     onChange={(e) => setEditing({ ...editing, separator: e.target.value })} />
            </div>
            <div className="field">
              <label>Ширина лічильника</label>
              <input type="number" min={1} max={10} value={editing.counter_width}
                     onChange={(e) => setEditing({ ...editing, counter_width: Number(e.target.value) })} />
            </div>
            <div className="field">
              <label>Скид лічильника</label>
              <select value={editing.reset_period}
                      onChange={(e) => setEditing({ ...editing, reset_period: e.target.value as ResetPeriod })}>
                {(['NEVER','YEAR','MONTH'] as ResetPeriod[]).map((p) =>
                  <option key={p} value={p}>{RESET_LABELS[p]}</option>
                )}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_year} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_year: e.target.checked })} />
                Додавати рік
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_month} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_month: e.target.checked })} />
                Додавати місяць
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_org_code} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_org_code: e.target.checked })} />
                Додавати код організації
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.per_organization} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, per_organization: e.target.checked })} />
                Окрема нумерація для кожної організації
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.is_active} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Активна
              </label>
            </div>
          </div>

          {previewQ.data && (
            <p style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                         background: 'var(--primary-soft)', color: 'var(--primary)',
                         borderRadius: 'var(--radius-sm)', fontFamily: 'monospace' }}>
              Наступний номер буде: <strong>{previewQ.data.preview}</strong>
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="primary" onClick={() => save.mutate()}
                    disabled={save.isPending}>
              Зберегти
            </button>
            <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <DataTable<NumberScheme>
        rows={schemesQ.data ?? []}
        columns={cols}
        rowKey={(s) => s.id}
        loading={schemesQ.isLoading}
        emptyText="Схем нема. Документи нумеруються дефолтним форматом ПРЕФІКС-РІК-NNNN (TS-2026-0001). Додайте схему, якщо потрібно змінити формат."
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
